// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The comfort block a wrist row and a settings card both read (G2-01, G2-02; 06 §6, NFR-023 …
// NFR-026).
//
// 06 §6 lists six accessibility commitments and the wrist menu offered **one row** for all of them:
// `comfort`, which toggled seated mode and nothing else. Reduce flashing and reduce motion existed
// as preferences with no headset control at all, and adjustable machine height existed nowhere —
// `grep -rn "heightOffset\|height offset\|HeightOffset" src/ native/ test/` returned nothing on
// 2026-09-03.
//
// **The shape is `audioPrefs.ts`'s, one row over**, and copying it rather than inventing one is the
// point: named steps, a label function per row, one latch each, one storage key each. That file
// already argued why three named steps beat a slider — "a control with a hundred positions,
// ninety-seven of which nobody can tell apart" — and a wrist row makes the argument twice over,
// because a row is pressed rather than dragged and a cycle through three states is the only
// interaction a row has.
//
// THE HEIGHT OFFSET IS A DROP, NEVER A HEIGHT
// --------------------------------------------
// **06 §3.1: "the whole reason this placement is head-referenced is that the floor could not be
// trusted."** A Quest whose space setup was done at a desk has reported eye heights of 3.44 m and
// −2.63 m in one session, both from a runtime that had cheerfully confirmed a floor-relative
// reference space. So there is no world height here to offer a player: what this adjusts is the
// **eye drop** — how far below the eyeline the lockdown bar sits — which is the quantity
// `seatedPlacement` already computes and the one that survives a tracking space being renumbered.
//
// **Independent of scale**, which 06 §6 asks for by name. The drop is a distance from the player's
// eyes to the bar, and a player's body does not shrink when the machine does: the tabletop scale
// already multiplies `lockdownBarHeight`, and adding a scaled offset on top would move the machine
// twice for one decision. This file's number is metres of drop and nothing multiplies it.

import { readLocal, removeLocal, safeStorage, writeLocal, type StorageLike } from '../storage/local'
import { stampPreference } from '../storage/prefStamps'
import { readFlashingChoice, writeFlashingChoice, type FlashingChoice } from './accessibility'
import { readMotionIntensity, writeMotionIntensity } from './accessibility'
import { MOTION_STEPS } from '../world/motionPreference'

/** Device-local posture choice. Explicit off differs from never having chosen. */
export const SEATED_PLAY_KEY = 'hcc.pinball.comfort.seated'

export function readSeatedPlay(storage: StorageLike | undefined = safeStorage()): boolean | undefined {
  const value = readLocal(storage, SEATED_PLAY_KEY)
  return value === '1' ? true : value === '0' ? false : undefined
}

export function writeSeatedPlay(on: boolean, storage: StorageLike | undefined = safeStorage()): void {
  writeLocal(storage, SEATED_PLAY_KEY, on ? '1' : '0')
}

// ---------------------------------------------------------------------------
// Height offset
// ---------------------------------------------------------------------------

/**
 * The five positions a player may put the machine at, relative to where it would otherwise go.
 *
 * Named steps rather than a slider, for `audioPrefs.ts`'s reason. Five rather than three because
 * this is the one comfort control whose right answer is a *body*: a tall player in a low chair and
 * a short player standing want opposite corrections, and three steps would give each of them one
 * usable direction.
 *
 * `level` is the default and is the placement the machine computes on its own.
 */
export const HEIGHT_OFFSETS = ['much-lower', 'lower', 'level', 'higher', 'much-higher'] as const
export type HeightOffset = (typeof HEIGHT_OFFSETS)[number]

/**
 * How far each step moves the bar, in metres of **eye drop**. Positive lowers the machine.
 *
 * **Judgement, not measurement**, and it stays judgement until somebody adjusts one on a headset
 * and says whether a step felt like a step. The size is derived rather than picked out of the air:
 * `SEATED_BAR_BELOW_EYELINE` is 0.30 m and the standing drop at a 1.71 m eyeline is about 0.76 m,
 * so one step of 0.06 m is roughly a fifth of the seated drop — visible in one press, and four
 * presses from end to end move the bar 0.24 m, which is about the difference between two chairs.
 *
 * Bounded by construction: the table has five entries, so the offset can never run away.
 */
export const HEIGHT_OFFSET_METRES: Readonly<Record<HeightOffset, number>> = {
  // Positive drop = further below the eyeline = a lower machine.
  'much-lower': 0.12,
  lower: 0.06,
  level: 0,
  higher: -0.06,
  'much-higher': -0.12,
}

export const HEIGHT_OFFSET_KEY = 'hcc.pinball.comfort.height'

/** What the player last chose, or `level`. An unreadable value is `level` rather than a throw. */
export function readHeightOffset(storage: StorageLike | undefined = safeStorage()): HeightOffset {
  const stored = readLocal(storage, HEIGHT_OFFSET_KEY)
  return (HEIGHT_OFFSETS as readonly string[]).includes(stored ?? '')
    ? (stored as HeightOffset)
    : 'level'
}

export function writeHeightOffset(
  offset: HeightOffset,
  storage: StorageLike | undefined = safeStorage(),
): void {
  // Stamped before either branch, for the reason `writeFlashingChoice` gives: `level` is a choice
  // and not an absence of one, so an account must not hand back whatever was set before it.
  stampPreference('height', storage)
  if (offset === 'level') {
    removeLocal(storage, HEIGHT_OFFSET_KEY)
    return
  }
  writeLocal(storage, HEIGHT_OFFSET_KEY, offset)
}

/**
 * The extra eye drop this player has asked for, in metres.
 *
 * The one question the placement asks. Added to the drop `seatedPlacement` computes, never
 * multiplied by the scale — see the header.
 */
export function heightDropOffset(offset: HeightOffset = readHeightOffset()): number {
  return HEIGHT_OFFSET_METRES[offset]
}

// ---------------------------------------------------------------------------
// The labels
// ---------------------------------------------------------------------------
//
// **One function per row, and the row draws exactly what comes out.** That is the rule M8-31 cost
// the product three times over: `soundMuted`, `tabletop` and `enteringInitials` each went stale in
// a hand-picked list of fields the wrist panel hashed to decide whether to repaint, and every one
// was invisible from the panel. A label function means the panel can key its repaint on **what
// would be drawn** rather than on the fields that feed it, and a label cannot go stale because the
// label is the drawing.

/** The height row's sentence. Says where the machine is, not what pressing will do. */
export function heightLabel(offset: HeightOffset): string {
  switch (offset) {
    case 'much-lower':
      return 'Machine much lower'
    case 'lower':
      return 'Machine lower'
    case 'higher':
      return 'Machine higher'
    case 'much-higher':
      return 'Machine much higher'
    default:
      return 'Machine height: level'
  }
}

/**
 * The flashing row's sentence.
 *
 * Three states and not two, because `system` is a choice: a row reading "Flashing: full" over a
 * machine the operating system has already quietened would be telling a player something untrue.
 */
export function flashingLabel(choice: FlashingChoice): string {
  switch (choice) {
    case 'on':
      return 'Less flashing'
    case 'off':
      return 'Full flashing'
    default:
      return 'Flashing: follow system'
  }
}

/**
 * Which of `MOTION_STEPS` a stored intensity is nearest.
 *
 * **The product's own four positions, not a second set of three.** `MachineView.cycleMotion` has
 * stepped through `[1, 0.5, 0.25, 0]` since M3-11, the settings page draws them as four buttons,
 * and `preferences.ts` validates the synced value against them — so a wrist row with its own
 * vocabulary would be a fifth opinion about a value three surfaces already agree on. The steps
 * moved to `motionPreference.ts` in this change; nothing about them did.
 *
 * A stored value between two steps reads as the nearer one rather than as an error: the number is
 * an intensity and any value in 0…1 is meaningful to the renderer, so a row that has to *name* a
 * state names the closest one it has a word for.
 */
export function motionStepOf(intensity: number = readMotionIntensity()): number {
  let best = MOTION_STEPS[0] ?? 1
  let gap = Number.POSITIVE_INFINITY
  for (const step of MOTION_STEPS) {
    const distance = Math.abs(step - intensity)
    if (distance < gap) {
      gap = distance
      best = step
    }
  }
  return best
}

/** The motion row's sentence, for one of `MOTION_STEPS`. */
export function motionLabel(step: number): string {
  if (step <= 0) return 'No camera motion'
  if (step >= 1) return 'Full motion'
  return step >= 0.5 ? 'Half motion' : 'A hint of motion'
}

// ---------------------------------------------------------------------------
// The latches
// ---------------------------------------------------------------------------
//
// One per row, each cycling forwards through its own list and wrapping. A wrist row has one
// interaction and this is it; the settings page offers the same values as buttons, reading and
// writing the same keys, so neither surface can show a value the other is not at.

const next = <T,>(steps: readonly T[], current: T): T =>
  steps[(steps.indexOf(current) + 1) % steps.length] as T

/** Cycle the height offset and store it. Answers what it is now. */
export function cycleHeightOffset(storage: StorageLike | undefined = safeStorage()): HeightOffset {
  const chosen = next(HEIGHT_OFFSETS, readHeightOffset(storage))
  writeHeightOffset(chosen, storage)
  return chosen
}

/** Cycle the flashing choice and store it. */
export function cycleFlashing(storage: StorageLike | undefined = safeStorage()): FlashingChoice {
  // `system → on → off`, so the first press of a row a player has never touched turns flashing
  // *down*. That direction is deliberate: somebody pressing a row called "Flashing" in a headset
  // is far more likely to be asking for less of it than for more.
  const order: readonly FlashingChoice[] = ['system', 'on', 'off']
  const chosen = next(order, readFlashingChoice(storage))
  writeFlashingChoice(chosen, storage)
  return chosen
}

/**
 * Cycle the motion intensity and store it. Answers the value the renderer will now read.
 *
 * Named `cycleMotionPreference` rather than `cycleMotion`, because `MachineView.cycleMotion` is a
 * different function with the same job on a different object — it moves the *live* intensity of one
 * view and stores nothing. Two exported `cycleMotion`s would be a name that means "step the motion"
 * in two places that do not agree about whether the choice survives the page.
 */
export function cycleMotionPreference(storage: StorageLike | undefined = safeStorage()): number {
  const chosen = next(MOTION_STEPS, motionStepOf(readMotionIntensity(storage)))
  writeMotionIntensity(chosen, storage)
  return chosen
}
